import { Inject } from '@odinjs/odin';
import { ActionAPI, MediaContainerAPI, MediaContainerData, ModalButton, ModalProps, ModalService, PanelAPI, PDFViewerAPI, TextAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { ModalButtonType, Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { FragmentNames } from '../../../codegen/constants/fragments/fragment-names';
import { NonLabExamDetailAliases } from '../../../codegen/constants/aliases/non-lab-exam-detail-aliases';

import { Exam } from '../../../interfaces/exam';
import { ReportStatus } from '../../../constants/enums/report-status';
import { ApplicationRoute } from '../../../constants/routes';
import PacsLinkGeneratorService from '../../../services/pacs-link-generator-service'
import ReportService from '../../../services/report-service';
import { FoundationUtils } from '../../../utils/foundation-utils';
import BaseController from '../../BaseController';
import { BrowserService } from '../../commons/browser/browser-service';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';

import './non-lab-exam-detail-controller.css';

@Controller({ fragment: FragmentNames.NON_LAB_EXAM_DETAIL })
class NonLabExamDetailController extends BaseController {

  @Inject(NonLabExamDetailAliases.PANEL_MEDIA_CONTAINER_PDF_VIEWER)
  private panelMediaContainerPDFViewerApi!: PanelAPI;

  @Inject(NonLabExamDetailAliases.PANEL_PDF_VIEWER_EXAM_DETAILS)
  private panelPdfViewerExamDetailsAPI!: PanelAPI;

  @Inject(NonLabExamDetailAliases.MEDIA_CONTAINER_NON_LAB)
  private mediaContainerApi!: MediaContainerAPI;

  @Inject(NonLabExamDetailAliases.PAC_BTN)
  private intgrationButtonPacs!: ActionAPI;

  @Inject(NonLabExamDetailAliases.TEXT_DT_EXAME)
  private examDateTextApi!: TextAPI;

  @Inject(NonLabExamDetailAliases.TEXT_DT_LAUDO)
  private reportDateTextApi!: TextAPI;

  @Inject(NonLabExamDetailAliases.PDF_VIEWER_EXAM_DETAILS)
  private pdfViewerApi!: PDFViewerAPI;

  @Inject(NonLabExamDetailAliases.PANEL_LOADER)
  private panelLoader!: PanelAPI;

  @OnMount(NonLabExamDetailAliases.LAYOUT_NON_LAB_EXAM_DETAIL)
  onMount(): void {

    InformationBarUtils.collapsedBar = true;

    const { patientId, encounterId, nrSequencia } = this.historyService.getCurrentRouteParams();

    this.context.set({
      browser: BrowserService.detectBrowser(),
      encounterId: encounterId,
      nrSequencia: nrSequencia,
      patientId: patientId,
    });
  }

  @OnMount(NonLabExamDetailAliases.PDF_VIEWER_EXAM_DETAILS)
  handleMountPdfViewer(): void {
    const file = this.context.get('reportFile')

    this.pdfViewerApi.setDownloadPdfExpression('tws.view-report');
    this.pdfViewerApi.setOpenPdfExpression('text.open-report');
    if (!file) {
      this.panelPdfViewerExamDetailsAPI.setVisible(false);
      return;
    }
    this.panelLoader.setVisible(false)
    this.pdfViewerApi.setVisible(true);
    this.pdfViewerApi.setSrc(file);
  }

  @OnMount(NonLabExamDetailAliases.MEDIA_CONTAINER_NON_LAB)
  handleMountMediaContainerNonLab(): void {
    const mediaContainerData = this.context.get('mediaContainerData') as MediaContainerData[]
    if (!mediaContainerData) {
      this.panelMediaContainerPDFViewerApi.setVisible(false);
      return;
    }
    this.panelLoader.setVisible(false)
    this.mediaContainerApi.setData(mediaContainerData);
  }

  @OnMount(NonLabExamDetailAliases.PAC_BTN)
  handleMountButtonPac(): void {
    const buttonPacs = this.getParamAccess('PACS_INTEGRATION_TYPE#10');

    if(buttonPacs === 'C') {
      this.intgrationButtonPacs.setEnabled(true);
    }
  }

  @OnClick(NonLabExamDetailAliases.IMAGE_VIEWER_BUTTON)
  handleClickImageViewerButton(): void {
    this.mediaContainerApi.open();
  }

  getParamAccess(parameter: string): string {
    return FoundationUtils.findModuleParam('PEP#9127', parameter) as string;
  }

  pacsButtonModal(): void {
    const buttons: ModalButton[] = [];

    buttons.push({
      action: () => {},
      title: 'tws.form.cancel',
      type: ModalButtonType.CANCEL,
    });

    buttons.push({
      action: () => { this.generateLinkPacs() },
      title: 'msg.continue',
      type: ModalButtonType.SUCCESS,
    });

    const dialogModal: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'text.dialog-open-pacs',
      dockButtons: buttons,
      focusOnOpen: false,
      onCancel: () => { },
      onSubmit: () => { },
      title: 'text.title-dialog-open-pacs',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogModal);
  }

  generateLinkPacs(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const nrAcessoDicom = this.context.get('nrAcessoDicom');
    const reportParams = this.context.get('reportParams');
    const pacsParams = {
      encounterId: String(encounterId),
      examId: nrAcessoDicom?.toString() || '',
      organizationId: reportParams.CD_ESTABELECIMENTO?.toString() || '',
      patientId: String(patientId)
    };
    PacsLinkGeneratorService.openPacsInBrowser(pacsParams);
  }

  @OnAfterFetch(NonLabExamDetailAliases.FETCH_NON_LAB_EXAM_DETAIL)
  onAfterFechNonLabExam(e: FetchEvent): void {
    const dataInformation = e.data as Exam;
    const formattedDtExame = ModelService.format('date', dataInformation.dtExame, { type: 'timestamp' });
    const formattedDtLaudo = ModelService.format('date', dataInformation.dtLaudo, { type: 'timestamp' });

    this.reportDateTextApi.setValue(formattedDtLaudo);
    this.examDateTextApi.setValue(formattedDtExame);
    const params = this.createReportData(dataInformation);
    this.context.set({ reportParams: params });
    this.handleReportLoading(dataInformation);
    this.context.set({ dsTituloLaudo: dataInformation.dsTituloLaudo });
    this.context.set({ nrAcessoDicom: dataInformation.nrAcessoDicom });
  }

  handleReportLoading(exam: Exam): void {
    if (exam.reportAvailable === ReportStatus.REPORT_NOT_AVAILABLE) {
      this.panelPdfViewerExamDetailsAPI.setVisible(false);
      this.panelMediaContainerPDFViewerApi.setVisible(false);
      this.panelLoader.setVisible(false);
      return;
    }

    this.loadReport(exam);
  }

  @OnClick(NonLabExamDetailAliases.PAC_BTN)
  OnClickPacBtn(): void {
    this.pacsButtonModal();
  }

  @OnClick(NonLabExamDetailAliases.ACTION_IMAGES)
  onButtonImagesClick(): void {
    const { nrSequencia } = this.historyService.getCurrentRouteParams();
    const dsTituloLaudo = this.context.get('dsTituloLaudo') as string;

    applicationContext.redirect(ApplicationRoute.ELECTRONIC_PATIENT_CHART +
      ApplicationRoute.PATIENT_LIST +
      ApplicationRoute.NON_LAB_EXAMS_LIST +
      ApplicationRoute.NON_LAB_EXAM_DETAIL +
      ApplicationRoute.NON_LAB_EXAM_IMAGES, { nrSequencia },
    {
      searchParams: {
        title: dsTituloLaudo
      }
    });
  }

  private createReportData(data: Exam): Record<string, Value> {
    return {
      CD_ESTABELECIMENTO: data.cdEstabelecimento || '',
      EXAM_NAME: data.dtLaudo,
      EXAM_TYPE: 'N',
      IE_FORMATO: data.ieFormato || 0,
      NR_ACESSO_DICOM: data.nrAcessoDicom || 0,
      NR_PRESCRICAO: data.nrPrescricao || '',
      NR_SEQ_RESULTADO: '',
      NR_SEQUENCIA_P: data.nrSequencia,
      VL_PARAM_STATUS_EXAME: 35,
    };
  }


  private loadReport(data: Exam): void {
    const params = this.createReportData(data);
    const reportService = new ReportService(this);
    this.context.set({ reportParams: params });

    reportService.generateReport(params).then(reportFile => {
      this.context.set({ reportFile });
    }).catch(() => {
      this.panelPdfViewerExamDetailsAPI.setVisible(false);
      this.errorReport();
    }).finally(() => {
      this.panelLoader.setVisible(false)
      this.panelPdfViewerExamDetailsAPI.setVisible(true)
    });
  }

  private errorReport(): void {
    ToastService.open({
      dismissible: true,
      id: 'prescription-report-toast',
      level: ToastLevel.ERROR,
      message: MessageService.translate('tws.error-generate-report'),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      title: MessageService.translate('tws.error-generic'),
      type: ToastType.BANNER,
    });
  }
}
export default NonLabExamDetailController;
